import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import isNil from 'lodash/isNil';

import type { Nullable } from '../../../services/object';

export type UserInContext = Readonly<{
  firstName: string;
  lastName: string;
  id: string;
}>;

type ContextValue = Readonly<{
  userInContext: UserInContext;
}>;

const context = createContext<Nullable<ContextValue>>(undefined);

export type Props = Readonly<{
  userInContext: UserInContext;
  children: ReactNode;
}>;

const UserInContextProvider = ({ userInContext, children }: Props): ReactElement => {
  const contextValue = useMemo<ContextValue>(() => ({
    userInContext,
  }), [userInContext]);

  return (
    <context.Provider value={contextValue}>
      {children}
    </context.Provider>
  );
};

export const useUserInContext = (): ContextValue => {
  const contextValue = useContext(context);

  if (isNil(contextValue)) {
    throw Error('Used `useUserInContextContract` without a provider');
  }

  return contextValue;
};

export default UserInContextProvider;
